import React, { useEffect, useState } from 'react';
import { depthChartAPI, playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Shield, Swords, Save, Lock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// Expanded positions
const OFFENSE_POSITIONS = ['QB', 'RB', 'IS_WR', 'OS_WR', 'TE', 'LT', 'LG', 'C', 'RG', 'RT'];
const DEFENSE_POSITIONS = ['Corner', 'Nickel', 'Safety', 'LB', 'DT', 'DE'];
const ALL_POSITIONS = [...OFFENSE_POSITIONS, ...DEFENSE_POSITIONS];

const POSITION_LABELS = {
  QB: 'Quarterbacks',
  RB: 'Running Backs',
  IS_WR: 'Inside WR',
  OS_WR: 'Outside WR',
  TE: 'Tight Ends',
  LT: 'Left Tackle',
  LG: 'Left Guard',
  C: 'Center',
  RG: 'Right Guard',
  RT: 'Right Tackle',
  Corner: 'Cornerback',
  Nickel: 'Nickel',
  Safety: 'Safety',
  LB: 'Linebackers',
  DT: 'Defensive Tackle',
  DE: 'Defensive End',
};

// Map depth chart positions to player roster positions (both abbreviations AND full names)
const POSITION_MAPPINGS = {
  QB: ['QB', 'QUARTERBACK'],
  RB: ['RB', 'RUNNING BACK', 'RUNNINGBACK'],
  IS_WR: ['WR', 'WIDE RECEIVER', 'WIDERECEIVER', 'RECEIVER'],
  OS_WR: ['WR', 'WIDE RECEIVER', 'WIDERECEIVER', 'RECEIVER'],
  TE: ['TE', 'TIGHT END', 'TIGHTEND'],
  LT: ['OL', 'OFFENSIVE LINE', 'OFFENSIVE LINEMAN', 'TACKLE', 'LEFT TACKLE', 'O-LINE'],
  LG: ['OL', 'OFFENSIVE LINE', 'OFFENSIVE LINEMAN', 'GUARD', 'LEFT GUARD', 'O-LINE'],
  C: ['OL', 'OFFENSIVE LINE', 'OFFENSIVE LINEMAN', 'CENTER', 'O-LINE'],
  RG: ['OL', 'OFFENSIVE LINE', 'OFFENSIVE LINEMAN', 'GUARD', 'RIGHT GUARD', 'O-LINE'],
  RT: ['OL', 'OFFENSIVE LINE', 'OFFENSIVE LINEMAN', 'TACKLE', 'RIGHT TACKLE', 'O-LINE'],
  Corner: ['DB', 'DEFENSIVE BACK', 'CORNER', 'CORNERBACK', 'CB'],
  Nickel: ['DB', 'DEFENSIVE BACK', 'NICKEL', 'NB'],
  Safety: ['DB', 'DEFENSIVE BACK', 'SAFETY', 'S', 'FS', 'SS', 'FREE SAFETY', 'STRONG SAFETY'],
  LB: ['LB', 'LINEBACKER', 'LINEBACKERS'],
  DT: ['DL', 'DEFENSIVE LINE', 'DEFENSIVE LINEMAN', 'TACKLE', 'DEFENSIVE TACKLE', 'D-LINE'],
  DE: ['DL', 'DEFENSIVE LINE', 'DEFENSIVE LINEMAN', 'END', 'DEFENSIVE END', 'D-LINE'],
};

// Map depth chart positions to player roster positions (kept for backward compatibility)
const POSITION_TO_PLAYER_POSITION = {
  QB: 'QB',
  RB: 'RB',
  IS_WR: 'WR',
  OS_WR: 'WR',
  TE: 'TE',
  LT: 'OL',
  LG: 'OL',
  C: 'OL',
  RG: 'OL',
  RT: 'OL',
  Corner: 'DB',
  Nickel: 'DB',
  Safety: 'DB',
  LB: 'LB',
  DT: 'DL',
  DE: 'DL',
};

// Map base player positions to depth chart categories for permission check
const BASE_TO_DEPTH_POSITIONS = {
  QB: ['QB'],
  RB: ['RB'],
  WR: ['IS_WR', 'OS_WR'],
  TE: ['TE'],
  OL: ['LT', 'LG', 'C', 'RG', 'RT'],
  DB: ['Corner', 'Nickel', 'Safety'],
  LB: ['LB'],
  DL: ['DT', 'DE'],
};

export default function DepthChartPage() {
  const { user } = useAuth();
  const [depthChart, setDepthChart] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [viewMode, setViewMode] = useState('all');
  const [editedChart, setEditedChart] = useState({});
  const [customInputs, setCustomInputs] = useState({}); // For "Other" custom names
  const [showCustomInput, setShowCustomInput] = useState({}); // Track which slots show custom input

  // Check if user can edit a position
  const canEditPosition = (position) => {
    if (!user) return false;
    if (['head_coach', 'coordinator', 'trainer'].includes(user.role)) return true;
    // Check if user's assigned position maps to this depth chart position
    const userBasePos = user.assigned_position?.toUpperCase();
    const allowedPositions = BASE_TO_DEPTH_POSITIONS[userBasePos] || [];
    return allowedPositions.includes(position);
  };

  const canViewAll = user && ['head_coach', 'coordinator', 'trainer'].includes(user.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [chartRes, playersRes] = await Promise.all([
        depthChartAPI.getAll(),
        playersAPI.getAll()
      ]);
      setDepthChart(chartRes.data);
      setPlayers(playersRes.data);
      
      const initial = {};
      chartRes.data.forEach(entry => {
        initial[entry.position] = {
          first_string: entry.first_string || '',
          second_string: entry.second_string || '',
          third_string: entry.third_string || '',
          fourth_string: entry.fourth_string || '',
          first_string_custom: entry.first_string_custom || '',
          second_string_custom: entry.second_string_custom || '',
          third_string_custom: entry.third_string_custom || '',
          fourth_string_custom: entry.fourth_string_custom || '',
        };
      });
      setEditedChart(initial);
    } catch (error) {
      toast.error('Failed to load depth chart');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (position, slot, value) => {
    if (value === 'other') {
      // Show custom input
      setShowCustomInput(prev => ({
        ...prev,
        [`${position}_${slot}`]: true
      }));
      setEditedChart(prev => ({
        ...prev,
        [position]: {
          ...prev[position],
          [slot]: ''
        }
      }));
    } else {
      // Regular player selection
      setShowCustomInput(prev => ({
        ...prev,
        [`${position}_${slot}`]: false
      }));
      setEditedChart(prev => ({
        ...prev,
        [position]: {
          ...prev[position],
          [slot]: value || null,
          [`${slot}_custom`]: '' // Clear custom name
        }
      }));
    }
  };

  const handleCustomNameChange = (position, slot, customName) => {
    setEditedChart(prev => ({
      ...prev,
      [position]: {
        ...prev[position],
        [`${slot}_custom`]: customName
      }
    }));
  };

  const handleSave = async (position) => {
    setSaving(prev => ({ ...prev, [position]: true }));
    try {
      await depthChartAPI.updatePosition(position, {
        position,
        ...editedChart[position]
      });
      toast.success(`${POSITION_LABELS[position]} depth chart saved`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save depth chart');
    } finally {
      setSaving(prev => ({ ...prev, [position]: false }));
    }
  };

  const getPlayersForPosition = (position) => {
    const validPositions = POSITION_MAPPINGS[position];
    if (!validPositions || validPositions.length === 0) return players;
    return players.filter(p => {
      const pPos = p.position?.toUpperCase()?.trim();
      if (!pPos) return false;
      // Check if player position matches any of the valid positions for this depth chart slot
      return validPositions.some(valid => pPos === valid || pPos.includes(valid) || valid.includes(pPos));
    });
  };

  const getDisplayName = (entry, slot) => {
    const customName = entry?.[`${slot}_custom`];
    if (customName) return customName;
    
    const playerData = entry?.[`${slot}_player`];
    if (playerData) {
      return `#${playerData.jersey_number} ${playerData.name}`;
    }
    return '--';
  };

  const DepthChartCard = ({ position }) => {
    const entry = depthChart.find(e => e.position === position) || {};
    const positionPlayers = getPlayersForPosition(position);
    const canEdit = canEditPosition(position);
    const currentValues = editedChart[position] || {};
    const isSaving = saving[position];

    return (
      <Card className="bg-[#121212] border-[#27272A]" data-testid={`depth-chart-${position}`}>
        <CardHeader className="border-b border-[#27272A] pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white text-base flex items-center gap-2">
              {OFFENSE_POSITIONS.includes(position) ? (
                <Swords className="w-4 h-4 text-[#FF3B30]" />
              ) : (
                <Shield className="w-4 h-4 text-[#007AFF]" />
              )}
              {POSITION_LABELS[position]}
            </CardTitle>
            {canEdit ? (
              <Button
                size="sm"
                onClick={() => handleSave(position)}
                disabled={isSaving}
                className="bg-[#32D74B] hover:bg-[#32D74B]/90 text-white text-xs px-2 py-1"
                data-testid={`save-${position}-btn`}
              >
                <Save className="w-3 h-3 mr-1" />
                {isSaving ? '...' : 'Save'}
              </Button>
            ) : (
              <div className="flex items-center gap-1 text-[#71717A] text-xs">
                <Lock className="w-3 h-3" />
                View
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          {['first_string', 'second_string', 'third_string', 'fourth_string'].map((slot, index) => {
            const showCustom = showCustomInput[`${position}_${slot}`];
            
            return (
              <div key={slot} className="flex items-center gap-2">
                <div className={`w-6 h-6 flex items-center justify-center font-bold text-xs ${
                  index === 0 ? 'bg-[#FFD60A] text-black' :
                  index === 1 ? 'bg-[#A1A1AA] text-black' :
                  index === 2 ? 'bg-[#CD7F32] text-white' :
                  'bg-[#3F3F46] text-white'
                }`}>
                  {index + 1}
                </div>
                {canEdit ? (
                  showCustom ? (
                    <div className="flex-1 flex gap-1">
                      <Input
                        value={currentValues[`${slot}_custom`] || ''}
                        onChange={(e) => handleCustomNameChange(position, slot, e.target.value)}
                        placeholder="Enter name..."
                        className="flex-1 bg-[#0A0A0A] border-[#27272A] text-white text-xs h-8"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowCustomInput(prev => ({ ...prev, [`${position}_${slot}`]: false }));
                          setEditedChart(prev => ({
                            ...prev,
                            [position]: { ...prev[position], [`${slot}_custom`]: '' }
                          }));
                        }}
                        className="text-[#71717A] h-8 px-2"
                      >
                        ×
                      </Button>
                    </div>
                  ) : (
                    <Select
                      value={currentValues[slot] || 'none'}
                      onValueChange={(value) => handleChange(position, slot, value === 'none' ? null : value)}
                    >
                      <SelectTrigger 
                        className="flex-1 bg-[#0A0A0A] border-[#27272A] text-white text-xs h-8"
                        data-testid={`${position}-${slot}-select`}
                      >
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-48">
                        <SelectItem value="none" className="text-[#71717A] text-xs">-- Empty --</SelectItem>
                        {positionPlayers.map(player => (
                          <SelectItem 
                            key={player.id} 
                            value={player.id}
                            className="text-white text-xs"
                          >
                            #{player.jersey_number} {player.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="other" className="text-[#FFD60A] text-xs">
                          <span className="flex items-center gap-1">
                            <UserPlus className="w-3 h-3" />
                            Other (type name)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  )
                ) : (
                  <div className="flex-1 px-2 py-1 bg-[#0A0A0A] border border-[#27272A] text-white text-xs">
                    {entry[`${slot}_custom`] || (entry[`${slot}_player`] ? (
                      <span>#{entry[`${slot}_player`].jersey_number} {entry[`${slot}_player`].name}</span>
                    ) : (
                      <span className="text-[#71717A]">--</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="depth-chart-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <p className="text-xs text-[#71717A] uppercase tracking-wider">Team</p>
            <h1 className="font-display text-4xl text-white tracking-tight">DEPTH CHART</h1>
            {user?.assigned_position && !canViewAll && (
              <p className="text-sm text-[#FFD60A] mt-1">
                You can edit: {user.assigned_position} positions
              </p>
            )}
          </div>

          {canViewAll && (
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white text-xs"
                  data-testid="view-all-tab"
                >
                  <Users className="w-3 h-3 mr-1" />
                  All
                </TabsTrigger>
                <TabsTrigger 
                  value="offense" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white text-xs"
                  data-testid="view-offense-tab"
                >
                  <Swords className="w-3 h-3 mr-1" />
                  Offense
                </TabsTrigger>
                <TabsTrigger 
                  value="defense" 
                  className="data-[state=active]:bg-[#007AFF] data-[state=active]:text-white text-xs"
                  data-testid="view-defense-tab"
                >
                  <Shield className="w-3 h-3 mr-1" />
                  Defense
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
          </div>
        ) : (
          <>
            {/* Offense Section */}
            {(viewMode === 'all' || viewMode === 'offense') && (
              <div>
                {canViewAll && viewMode === 'all' && (
                  <div className="flex items-center gap-2 mb-4">
                    <Swords className="w-5 h-5 text-[#FF3B30]" />
                    <h2 className="font-display text-2xl text-white">OFFENSE</h2>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {OFFENSE_POSITIONS.filter(pos => 
                    canViewAll || canEditPosition(pos)
                  ).map(position => (
                    <DepthChartCard key={position} position={position} />
                  ))}
                </div>
              </div>
            )}

            {/* Defense Section */}
            {(viewMode === 'all' || viewMode === 'defense') && (
              <div className={viewMode === 'all' ? 'mt-8' : ''}>
                {canViewAll && viewMode === 'all' && (
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-[#007AFF]" />
                    <h2 className="font-display text-2xl text-white">DEFENSE</h2>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                  {DEFENSE_POSITIONS.filter(pos => 
                    canViewAll || canEditPosition(pos)
                  ).map(position => (
                    <DepthChartCard key={position} position={position} />
                  ))}
                </div>
              </div>
            )}

            {!canViewAll && !user?.assigned_position && (
              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="p-8 text-center">
                  <Lock className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
                  <p className="text-white text-lg mb-2">No Position Assigned</p>
                  <p className="text-[#71717A]">
                    Ask the Head Coach or Trainer to assign you a position to edit the depth chart.
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
